/**
 * Teacher → Students
 *
 * Flat searchable list of every student across the teacher's classes.
 * Click a row → drill into the per-student profile (attendance %, recent
 * grades, submission count, risk flags).
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Search, AlertTriangle, User, Phone, Mail, GraduationCap, Loader2, X,
} from 'lucide-react';
import { portalApi } from './teacherApi';

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : '';

const StudentDetailDialog = ({ studentId, open, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['teacher-student-profile', studentId],
    queryFn: () => portalApi(`/teacher/students/${studentId}/profile`),
    enabled: open && !!studentId,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="teacher-student-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-indigo-500" />
            {data?.student ? `${data.student.first_name} ${data.student.last_name}` : 'Student profile'}
          </DialogTitle>
        </DialogHeader>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Admission:</span> <strong>{data.student.admission_number || '—'}</strong></div>
              <div><span className="text-slate-500">Class:</span> {data.student.class_name || data.student.class_id || '—'}</div>
              <div><span className="text-slate-500">Phone:</span> {data.student.phone || '—'}</div>
              <div><span className="text-slate-500">Email:</span> {data.student.email || '—'}</div>
            </div>

            <Card>
              <CardContent className="pt-4">
                <h4 className="text-sm font-semibold mb-2">Attendance · last 30 days</h4>
                {data.attendance.rate_pct != null ? (
                  <div className="flex items-center gap-3">
                    <div className="text-2xl font-bold">{data.attendance.rate_pct}%</div>
                    <div className="flex-1 bg-slate-200 rounded h-2 overflow-hidden">
                      <div className="bg-emerald-500 h-full" style={{ width: `${data.attendance.rate_pct}%` }} />
                    </div>
                    <Badge variant={data.attendance.rate_pct >= 85 ? 'default' : 'destructive'}>
                      {data.attendance.rate_pct >= 85 ? 'on track' : 'low'}
                    </Badge>
                  </div>
                ) : <p className="text-sm text-slate-400">No attendance recorded.</p>}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <h4 className="text-sm font-semibold mb-2">Recent grades in my subjects</h4>
                {data.grades.length ? (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 border-b">
                      <tr><th className="text-left py-1">Date</th><th className="text-left">Exam</th><th className="text-right">%</th><th>Grade</th></tr>
                    </thead>
                    <tbody>
                      {data.grades.map((g) => (
                        <tr key={g.id} className="border-b last:border-0">
                          <td className="py-1">{fmt(g.created_at)}</td>
                          <td>{g.exam_name || '—'}</td>
                          <td className="text-right">{g.percentage != null ? `${g.percentage}%` : '—'}</td>
                          <td className="text-center"><Badge variant="outline">{g.grade || '—'}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="text-sm text-slate-400">No grades yet.</p>}
              </CardContent>
            </Card>

            {data.risk && (
              <Card className="border-rose-300 bg-rose-50">
                <CardContent className="pt-4 text-sm">
                  <h4 className="text-sm font-semibold flex items-center gap-1 text-rose-700">
                    <AlertTriangle className="h-4 w-4" /> Flagged at risk
                  </h4>
                  <p className="text-rose-700/80 mt-1">{data.risk.reason || data.risk.note || 'Risk flag set by analytics engine'}</p>
                </CardContent>
              </Card>
            )}

            <p className="text-xs text-slate-500">Submissions on record: <strong>{data.submitted_count}</strong></p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export const TeacherStudents = () => {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const { data: students = [], isLoading } = useQuery({
    queryKey: ['teacher-students', search],
    queryFn: () => portalApi(`/teacher/students${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });

  return (
    <div className="space-y-3" data-testid="teacher-students-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">Students <Badge variant="outline">{students.length}</Badge></h2>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-8" placeholder="Search name / admission #…"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="teacher-students-search" />
        </div>
      </div>

      {isLoading ? (
        <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>
      ) : students.length === 0 ? (
        <p className="text-center text-slate-500 p-6">No students match.</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2.5">Name</th>
                  <th className="text-left p-2.5">Admission #</th>
                  <th className="text-left p-2.5">Class</th>
                  <th className="text-center p-2.5">Risk</th>
                  <th className="text-right p-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`teacher-student-row-${s.id}`}>
                    <td className="p-2.5 font-medium">{s.full_name}</td>
                    <td className="p-2.5 text-slate-600">{s.admission_number || '—'}</td>
                    <td className="p-2.5 text-slate-600">{s.class_name || '—'}</td>
                    <td className="p-2.5 text-center">
                      {s.at_risk
                        ? <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />at risk</Badge>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2.5 text-right">
                      <Button size="sm" variant="outline" onClick={() => setPicked(s.id)}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <StudentDetailDialog studentId={picked} open={!!picked} onClose={() => setPicked(null)} />
    </div>
  );
};

export default TeacherStudents;
